import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import Bubble from './Bubble';

function ClickSync() {
  const [bubbles, setBubbles] = useState([]);
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [playerCount, setPlayerCount] = useState(0);
  const gameAreaRef = useRef(null);
  const [gameAreaSize, setGameAreaSize] = useState({ width: 0, height: 0 });

  // Authentication state
  const [user, setUser] = useState(null);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'
  const [authForm, setAuthForm] = useState({ username: '', password: '', email: '' });
  const [authError, setAuthError] = useState('');

  // Generate random bubble name by combining adjectives and nouns
  const generateRandomBubbleName = () => {
    const adjectives = [
      'Chaotic', 'Mystic', 'Plasma', 'Quantum', 'Cosmic',
      'Stellar', 'Nebula', 'Galactic', 'Solar', 'Meteor',
      'Thunder', 'Lightning', 'Storm', 'Hurricane', 'Tornado',
      'Blazing', 'Frozen', 'Crystal', 'Diamond', 'Golden',
      'Shadow', 'Phantom', 'Ghost', 'Spirit', 'Ethereal',
      'Crimson', 'Azure', 'Violet', 'Emerald', 'Amber',
      'Radiant', 'Twisted', 'Ancient', 'Primal', 'Divine',
      'Cursed', 'Blessed', 'Feral', 'Wild', 'Tame',
      'Electric', 'Magnetic', 'Toxic', 'Pure', 'Corrupt'
    ];

    const nouns = [
      'Orb', 'Sphere', 'Ball', 'Bubble', 'Void',
      'Burst', 'Drop', 'Gem', 'Flare', 'Cell',
      'Ember', 'Pearl', 'Stone', 'Core', 'Heart',
      'Shard', 'Fragment', 'Crystal', 'Prism', 'Globe',
      'Node', 'Nexus', 'Vortex', 'Portal', 'Gate',
      'Ring', 'Crown', 'Shield', 'Blade', 'Spear',
      'Star', 'Moon', 'Sun', 'Comet', 'Nova',
      'Wave', 'Pulse', 'Echo', 'Whisper', 'Roar'
    ];

    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
    return `${randomAdjective} ${randomNoun}`;
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      if (entries.length > 0) {
        const { width, height } = entries[0].contentRect;
        setGameAreaSize({ width, height });
      }
    });

    const gameAreaElement = gameAreaRef.current;
    if (gameAreaElement) {
      resizeObserver.observe(gameAreaElement);
      // Set initial size
      const { width, height } = gameAreaElement.getBoundingClientRect();
      setGameAreaSize({ width, height });
    }

    return () => {
      if (gameAreaElement) {
        resizeObserver.unobserve(gameAreaElement);
      }
    };
  }, []); // Empty dependency array, runs once


  useEffect(() => {
    // Connect to backend - use localhost for development, will be Render URL later
    const newSocket = io(process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080', {
      withCredentials: true
    });

    newSocket.on('connect', () => {
      console.log('Connected to server');
      setConnected(true);
      // Request current player count when connecting
      newSocket.emit('getPlayerCount');
    });

    newSocket.on('disconnect', () => {
      console.log('Disconnected from server');
      setConnected(false);
    });

    // Listen for bubble updates from server
    newSocket.on('bubblesUpdate', (data) => {
      setBubbles(data.bubbles);
      // console.log('Bubbles updated:', data.reason, data.bubbles);
    });

    // Listen for player count updates
    newSocket.on('playerCountUpdate', (data) => {
      setPlayerCount(data.count);
    });

    setSocket(newSocket);

    // Cleanup on unmount
    return () => {
      newSocket.close();
    };
  }, []);

  const handleBubbleClick = (bubbleId, event) => {
    event.stopPropagation(); // Prevent event from reaching game area
    if (socket && connected) {
      // Check for modifier keys for god powers
      if (event.shiftKey) {
        // Shift+Click: Inflate by 10 (server is authoritative)
        socket.emit('godInflate', { bubbleId });
      } else if (event.ctrlKey || event.metaKey) {
        // Ctrl+Click (or Cmd+Click on Mac): Deflate by 10 (server is authoritative)
        socket.emit('godDeflate', { bubbleId });
      } else if (event.altKey) {
        // Alt+Click: Instantly pop bubble
        socket.emit('godPop', { bubbleId });
      } else {
        // Normal click: Inflate by 1
        socket.emit('bubbleClick', { bubbleId });
      }
    }
  };

  const handleBubbleDoubleClick = (event) => {
    event.stopPropagation(); // Prevent double-click from creating new bubble
  };

  const handleGameAreaDoubleClick = (event) => {
    if (socket && connected && gameAreaRef.current) {
      // Calculate click position relative to game area as percentages
      const rect = gameAreaRef.current.getBoundingClientRect();
      const xPercent = (event.clientX - rect.left) / rect.width;
      const yPercent = (event.clientY - rect.top) / rect.height;

      // Prompt for bubble name with random suggestion
      const name = prompt('Name your bubble:', generateRandomBubbleName());

      // Only create bubble if user didn't cancel
      if (name !== null) {
        const bubbleName = name.trim() || `Unnamed ${Date.now() % 1000}`;
        console.log('Creating bubble:', bubbleName, 'at:', `${(xPercent * 100).toFixed(1)}%, ${(yPercent * 100).toFixed(1)}%`);
        socket.emit('createBubble', { xPercent, yPercent, name: bubbleName });
      }
    }
  };

  const handleCreateRandomBubble = () => {
    if (socket && connected) {
      const randomName = generateRandomBubbleName();

      console.log('Requesting random bubble creation:', randomName);
      socket.emit('createRandomBubble', {
        name: randomName
      });
    }
  };

  // Authentication functions
  const checkAuthStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080'}/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      }
    } catch (error) {
      console.log('Not authenticated');
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');

    const endpoint = authMode === 'login' ? '/auth/login' : '/auth/register';
    const payload = authMode === 'login'
      ? { username: authForm.username, password: authForm.password }
      : authForm;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080'}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        setUser(data.user);
        setAuthForm({ username: '', password: '', email: '' });
        console.log(`${authMode === 'login' ? 'Logged in' : 'Registered'} successfully:`, data.user.username);
      } else {
        setAuthError(data.error || 'Authentication failed');
      }
    } catch (error) {
      setAuthError('Network error');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080'}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      setUser(null);
      console.log('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Check auth status on component mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const totalBubbles = bubbles.length;
  const totalSize = bubbles.reduce((sum, bubble) => sum + bubble.size, 0);

  // Helper function to get direction arrow based on velocity
  const getDirectionArrow = (dx, dy) => {
    const angle = Math.atan2(dy, dx);
    const degrees = (angle * 180 / Math.PI + 360) % 360;

    if (degrees >= 337.5 || degrees < 22.5) return '→';      // Right
    if (degrees >= 22.5 && degrees < 67.5) return '↘';       // Down-Right
    if (degrees >= 67.5 && degrees < 112.5) return '↓';      // Down
    if (degrees >= 112.5 && degrees < 157.5) return '↙';     // Down-Left
    if (degrees >= 157.5 && degrees < 202.5) return '←';     // Left
    if (degrees >= 202.5 && degrees < 247.5) return '↖';     // Up-Left
    if (degrees >= 247.5 && degrees < 292.5) return '↑';     // Up
    if (degrees >= 292.5 && degrees < 337.5) return '↗';     // Up-Right
    return '•'; // Fallback
  };

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Future: Left sidebar can be added here */}

      {/* Main Game Area Container */}
      <div style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
        {/* Future: Top sidebar can be added here */}

        <div style={{ flex: '1', padding: '20px', paddingRight: '10px', display: 'flex', flexDirection: 'column' }}>
        <div
          ref={gameAreaRef}
          onDoubleClick={handleGameAreaDoubleClick}
          style={{
            width: '100%',
            height: '100%',
            border: '2px solid #333',
            borderRadius: '10px',
            backgroundColor: '#f0f8ff',
            position: 'relative',
            cursor: connected ? 'crosshair' : 'not-allowed'
          }}
        >
        {/* Render all bubbles */}
        {bubbles.map(bubble => {
          const minDimension = Math.min(gameAreaSize.width, gameAreaSize.height);
          const bubbleDiameter = minDimension > 0
            ? Math.max(20, (bubble.size / 1000) * minDimension)
            : Math.max(20, bubble.size * 2);

          const xPercent = bubble.xPercent !== undefined ? bubble.xPercent * 100 : (bubble.x / 1000) * 100;
          const yPercent = bubble.yPercent !== undefined ? bubble.yPercent * 100 : (bubble.y / 600) * 100;

          const xPixels = (xPercent / 100) * gameAreaSize.width;
          const yPixels = (yPercent / 100) * gameAreaSize.height;

          return (
            <Bubble
              key={bubble.id}
              x={xPixels - bubbleDiameter/2}
              y={yPixels - bubbleDiameter/2}
              size={bubbleDiameter}
              team={bubble.team || "default"}
              color={bubble.color}
              name={bubble.name}
              bubbleSize={bubble.size}
              velocity={bubble.dx !== undefined && bubble.dy !== undefined ?
                { dx: bubble.dx, dy: bubble.dy, arrow: getDirectionArrow(bubble.dx, bubble.dy) } : null}
              connected={connected}
              onClick={(e) => handleBubbleClick(bubble.id, e)}
              onDoubleClick={handleBubbleDoubleClick}
            />
          );
        })}
        </div>

        {/* Future: Bottom sidebar can be added here */}
        </div>
      </div>

      {/* Right Sidebar */}
      <div style={{
        width: '300px',
        minWidth: '300px',
        padding: '20px',
        paddingLeft: '10px',
        backgroundColor: '#f9f9f9',
        borderLeft: '2px solid #ddd',
        overflowY: 'auto'
      }}>
        <h1 style={{ fontSize: '24px', marginBottom: '20px' }}>Pop Pop Chaos</h1>

        <div style={{ marginBottom: '20px' }}>
          <p><strong>Status:</strong> {connected ? '🟢 Connected' : '🔴 Disconnected'}</p>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ fontSize: '18px' }}>{totalBubbles} Bubbles</h2>
          <p>Total Size: {totalSize}</p>
          <p style={{ fontSize: '12px', color: '#666' }}>
            Players: {playerCount}
          </p>
        </div>

        <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '10px', color: '#333' }}>Controls</h3>
          <p><strong>Click bubbles</strong> to inflate them (+1)</p>
          <p><strong>Double-click empty space</strong> to create new bubbles</p>

          <h4 style={{ fontSize: '14px', marginTop: '20px', marginBottom: '8px', color: '#333' }}>God Powers:</h4>
          <p><em>Shift+Click</em> → +10 size</p>
          <p><em>Ctrl+Click</em> → -10 size</p>

          <div style={{ marginTop: '30px', padding: '15px', backgroundColor: '#fff3cd', borderRadius: '5px', border: '1px solid #ffeaa7' }}>
            <p style={{ margin: 0, fontSize: '13px' }}>
              <strong>🫧 Tip:</strong> Watch them shrink over time - keep them alive!
            </p>
          </div>

          {/* Authentication Section */}
          {!user ? (
            <div style={{ marginTop: '30px', padding: '15px', backgroundColor: '#e6f3ff', borderRadius: '5px', border: '1px solid #cce7ff' }}>
              <h4 style={{ fontSize: '14px', marginTop: '0px', marginBottom: '12px', color: '#0066cc' }}>
                🔐 {authMode === 'login' ? 'Login' : 'Register'}
              </h4>

              {authError && (
                <div style={{ padding: '8px', backgroundColor: '#ffe6e6', color: '#cc0000', borderRadius: '3px', fontSize: '12px', marginBottom: '10px' }}>
                  {authError}
                </div>
              )}

              <form onSubmit={handleAuth}>
                <input
                  type="text"
                  placeholder="Username"
                  value={authForm.username}
                  onChange={(e) => setAuthForm({...authForm, username: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '8px',
                    marginBottom: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '3px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  required
                />

                <input
                  type="password"
                  placeholder="Password"
                  value={authForm.password}
                  onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '8px',
                    marginBottom: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '3px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  required
                />

                {authMode === 'register' && (
                  <input
                    type="email"
                    placeholder="Email (optional)"
                    value={authForm.email}
                    onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '8px',
                      marginBottom: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '3px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                )}

                <button
                  type="submit"
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#0066cc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    marginBottom: '8px'
                  }}
                >
                  {authMode === 'login' ? 'Login' : 'Register'}
                </button>
              </form>

              <button
                onClick={() => {
                  setAuthMode(authMode === 'login' ? 'register' : 'login');
                  setAuthError('');
                  setAuthForm({ username: '', password: '', email: '' });
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: 'transparent',
                  color: '#0066cc',
                  border: '1px solid #0066cc',
                  borderRadius: '3px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                {authMode === 'login' ? 'Need an account? Register' : 'Have an account? Login'}
              </button>
            </div>
          ) : (
            <div style={{ marginTop: '30px', padding: '15px', backgroundColor: '#e6ffe6', borderRadius: '5px', border: '1px solid #ccffcc' }}>
              <h4 style={{ fontSize: '14px', marginTop: '0px', marginBottom: '12px', color: '#006600' }}>
                👤 Welcome, {user.username}!
              </h4>
              <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                User ID: {user.user_id} {user.is_superadmin && '(Superadmin)'}
              </p>

              {user.is_superadmin && (
                <>
                  <button
                    onClick={handleCreateRandomBubble}
                    disabled={!connected}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: connected ? '#ff6b6b' : '#cccccc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: connected ? 'pointer' : 'not-allowed',
                      marginBottom: '8px'
                    }}
                  >
                    🎲 Create Random Bubble
                  </button>
                  <p style={{ fontSize: '11px', color: '#666', marginBottom: '10px' }}>
                    Generates bubble with random properties
                  </p>
                  <p><em>Alt+Click</em> → 💥 instant pop</p>
                  <p style={{ fontSize: '11px', color: '#666', marginTop: '10px', fontFamily: 'monospace' }}>
                    Backend: {process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080'}
                  </p>
                </>
              )}

              <button
                onClick={handleLogout}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: 'transparent',
                  color: '#666',
                  border: '1px solid #999',
                  borderRadius: '3px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ClickSync;
